import _ from 'lodash';
import {api} from '@/helpers/api';
import {submitStatus, getFormattedAddress, addHomeDetails, progressStatus} from '@/helpers/utils';
import {
    HOME_MUTATIONS,
    SEARCH_MUTATIONS,
    blankAddHomeForm
} from './constants';

// State
export const state = {
    homeToEdit: null,
    homeSubmitted: null,
    photoDeleteStatus: null,
    homeDeleteStatus: null,
    autofilledLocation: null,
    autofillStatus: null,
    addressSearchCoordinates: null,
    addressSearchStatus: null,
    createdHomeId: null,
    location: { label: 'Location', component: 'LocationForm' },
    selectDetails: { label: 'Choose Details', component: 'SelectDetails' },
    occupants: null,
    gad: null,
    census: null,
    structure: null,
    events: null,
    photos: null,
    references: null,
    review: { label: 'Review', component: 'ReviewDetailWrapper'},
    submit: { label: 'Submit', component: 'SubmitSection' },
};

// Mutations
export const mutations = {
    [HOME_MUTATIONS.OVERWRITE_HOME_FORM](state, houseData) {
        Object.entries(houseData).forEach(([key, value]) => {
            state[key] = value;
        })
    },
    [HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS](state, status) {
        state.homeSubmitted = status;
    },
    [HOME_MUTATIONS.SET_CREATED_HOME_ID](state, id) {
        state.createdHomeId = id;
    },
    [HOME_MUTATIONS.SET_PHOTO_DELETE_STATUS](state, status) {
        state.photoDeleteStatus = status;
    },
    [HOME_MUTATIONS.SET_HOME_DELETE_STATUS](state, status) {
        state.homeDeleteStatus = status;
    },
    [HOME_MUTATIONS.SET_AUTOFILLED_LOCATION](state, location) {
        state.autofilledLocation = location;
    },
    [HOME_MUTATIONS.SET_AUTOFILL_STATUS](state, status) {
        state.autofillStatus = status;
    },
    [HOME_MUTATIONS.SET_ADDRESS_SEARCH_COORDINATES](state, coordinates) {
        state.addressSearchCoordinates = coordinates;
    },
    [HOME_MUTATIONS.SET_ADDRESS_SEARCH_STATUS](state, status) {
        state.addressSearchStatus = status;
    },
    [HOME_MUTATIONS.TOGGLE_EXTRA](state, key) {
        if (state[key] == null) {
            state[key] = addHomeDetails[key];
        } else {
            state[key] = null;
        }
    },
    [HOME_MUTATIONS.UPDATE_FORM_SECTION](state, formData) {
        const {key, data} = formData;
        state[key] = {...state[key], ...data};
    },
    [HOME_MUTATIONS.RESET_SUBMIT_STATUS](state) {
        state.review = _.cloneDeep(blankAddHomeForm.review);
        state.submit = _.cloneDeep(blankAddHomeForm.submit);
    },
    [HOME_MUTATIONS.SET_HOME_TO_EDIT](state, home) {
        // if there are any -1s left over from the android app, remove them
        const {structure, events, censuses} = home;

        if (home.census) {
            // old format. Update to new.
            let censusEmpty = true;
            Object.values(home.census).forEach(value => {
                const isEmptyArray = Array.isArray(value) && value.length === 0;
                if (value !== null && value !== "" && !isEmptyArray) {
                    censusEmpty = false;
                }
            });

            if (!censusEmpty) {
                censuses.push(home.census);
            }
            home.census = null;
        }

        censuses.forEach(censusItem => {
            if (censusItem.year && censusItem.year === -1) {
                censusItem.year = null;
            }
        });

        const intStructFields = ['year', 'numRooms', 'numWindows', 'numDoors'];
        Object.keys(structure).forEach(key => {
            if (intStructFields.includes(key) && structure[key] === -1) {
                structure[key] = null
            }
        });

        events.forEach(event => {
            if (event.year === -1) {
                event.year = null;
            }
        });

        state.homeToEdit = home;
        // Copy home data to the form in the proper format
        state.location.data = {
            country: home.country,
            latitude: home.latitude,
            longitude: home.longitude
        }
        state.occupants = home.occupants.length > 0 ? {
            ...addHomeDetails.occupants,
            data: home.occupants
        } : null;
        state.gad = !Object.values(home.gad).every(val => val === null) ? {
            ...addHomeDetails.gad,
            data: home.gad
        } : null;
        state.census = home.censuses.length > 0 ? {
            ...addHomeDetails.census,
            data: home.censuses
        } : null;
        state.structure = !Object.values(home.structure).every(val => val === null) ? {
            ...addHomeDetails.structure,
            data: home.structure
        } : null;
        state.events = home.events.length > 0 ? {
            ...addHomeDetails.events,
            data: home.events
        } : null;
        state.photos = home.photos.length > 0 ? {
            ...addHomeDetails.photos,
            data: home.photos
        } : null;
        state.references = home.references.length > 0 ? {
            ...addHomeDetails.references,
            data: home.references
        } : null;

        // Mark parts of form that have been filled out as done
        Object.keys(blankAddHomeForm).forEach((key) => {
            if (!['submit', 'review'].includes(key) && state[key] !== null) {
                state[key].status = progressStatus.DONE;
            }
        })
    },
};

// Actions
export const actions = {
    toggleAddFormExtra(context, key) {
        context.commit(HOME_MUTATIONS.TOGGLE_EXTRA, key);
    },
    overwriteForm: (context, houseData) => {
        context.commit(HOME_MUTATIONS.OVERWRITE_HOME_FORM, houseData);
    },
    clearForm: (context) => {
        context.commit(HOME_MUTATIONS.OVERWRITE_HOME_FORM, _.cloneDeep(blankAddHomeForm));
    },
    setHomeToEdit(context, home) {
        context.commit(HOME_MUTATIONS.SET_HOME_TO_EDIT, home);
    },
    submitHome: (context, houseData) => {
        context.commit(HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS, submitStatus.LOADING);
        context.commit(HOME_MUTATIONS.SET_CREATED_HOME_ID, null);

        api.submitHome(houseData).then(result => {
            if ('errors' in result) {
                context.commit(HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS, submitStatus.FAILED);
            } else {
                context.commit(HOME_MUTATIONS.SET_CREATED_HOME_ID, result._id);
                context.commit(HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS, submitStatus.SUCCESS);
            }
        })
    },
    bulkSubmitHomes: (context, houseList) => {
        context.commit(HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS, submitStatus.LOADING);

        api.bulkSubmitHomes(houseList).then(result => {
            if ('errors' in result) {
                context.commit(HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS, submitStatus.FAILED);
            } else {
                context.commit(HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS, submitStatus.SUCCESS);
            }
        })
    },
    resetSubmitStatus: (context) => {
        // use this one
        context.commit(HOME_MUTATIONS.RESET_SUBMIT_STATUS);
    },
    updateHome: (context, houseData) => {
        context.commit(HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS, submitStatus.LOADING);
        api.updateHome(houseData.data, houseData.id).then(result => {
            if ('errors' in result) {
                context.commit(HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS, submitStatus.FAILED);
            } else {
                context.commit(HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS, submitStatus.SUCCESS);
            }
        })
    },
    deleteHome: (context, homeId) => {
        context.commit(HOME_MUTATIONS.SET_HOME_DELETE_STATUS, submitStatus.LOADING);
        api.deleteHome(homeId).then(result => {
            if ('errors' in result) {
                context.commit(HOME_MUTATIONS.SET_HOME_DELETE_STATUS, submitStatus.FAILED);
            } else {
                context.commit(HOME_MUTATIONS.SET_HOME_DELETE_STATUS, submitStatus.SUCCESS);
                context.commit(SEARCH_MUTATIONS.REMOVE_SEARCH_RESULT, homeId);
            }
        })
    },
    // Create/edit home page - process form data
    resetHomeSubmitStatus(context) {
        // this is the old version used in bulk uploads
        context.commit(HOME_MUTATIONS.SET_HOME_SUBMIT_STATUS, null);
    },
    autofillLocation(context, coordinates) {
        context.commit(HOME_MUTATIONS.SET_AUTOFILL_STATUS, submitStatus.LOADING);

        api.autofillLocation(coordinates).then(result => {
            if (result.status === 200) {
                context.commit(HOME_MUTATIONS.SET_AUTOFILL_STATUS, submitStatus.SUCCESS);
                const formattedAddress = getFormattedAddress(result.data.results);
                context.commit(HOME_MUTATIONS.SET_AUTOFILLED_LOCATION, formattedAddress);
            } else {
                context.commit(HOME_MUTATIONS.SET_AUTOFILL_STATUS, submitStatus.FAILED)
            }

        });
    },
    clearAddressSearch(context) {
        context.commit(HOME_MUTATIONS.SET_ADDRESS_SEARCH_STATUS, null);
    },
    searchByAddress(context, address) {
        context.commit(HOME_MUTATIONS.SET_ADDRESS_SEARCH_STATUS, submitStatus.LOADING);

        api.searchByAddress(address).then(result => {
            // clear out any existing search results
            context.commit(HOME_MUTATIONS.SET_ADDRESS_SEARCH_COORDINATES, null);
            if (result.status === 200) {
                try {
                    const results = result.data.results;
                    const formattedAddress = getFormattedAddress(results);
                    const locationObj = {...results[0].geometry.location, country: formattedAddress.country};
                    context.commit(HOME_MUTATIONS.SET_ADDRESS_SEARCH_COORDINATES, locationObj);
                    context.commit(HOME_MUTATIONS.SET_ADDRESS_SEARCH_STATUS, submitStatus.SUCCESS);
                } catch (e) {
                    context.commit(HOME_MUTATIONS.SET_ADDRESS_SEARCH_STATUS, submitStatus.FAILED);
                }
            } else {
                context.commit(HOME_MUTATIONS.SET_ADDRESS_SEARCH_STATUS, submitStatus.FAILED);
            }
        })
    },
    deletePhoto(context, homeToDelete) {
        context.commit(HOME_MUTATIONS.SET_PHOTO_DELETE_STATUS, submitStatus.LOADING);

        api.deletePhoto(homeToDelete).then(result => {
            if ('error' in result) {
                context.commit(HOME_MUTATIONS.SET_PHOTO_DELETE_STATUS, submitStatus.FAILED);
            } else {
                context.commit(HOME_MUTATIONS.SET_PHOTO_DELETE_STATUS, submitStatus.SUCCESS);
            }
        })
    },
    updateFormSection(context, {key, data}) {
        context.commit(HOME_MUTATIONS.UPDATE_FORM_SECTION, {key, data});
    },
};

// Getters
export const getters = {
    homeSubmitStatus(state) {
        return state.homeSubmitted;
    },
    homeDeleteStatus(state) {
        return state.homeDeleteStatus;
    },
    formIsEmpty(state) {
        for(const key of Object.keys(blankAddHomeForm)){
            const inputVal = state[key];
            const blank = blankAddHomeForm[key];

            if(!_.isEqual(inputVal, blank)){
                return false;
            }
        }
        return true;
    },
    getHomeToEdit(state) {
        return state.homeToEdit;
    },
    getPhotoDeleteStatus(state) {
        return state.photoDeleteStatus;
    },
    getAutofilledLocation(state) {
        return state.autofilledLocation;
    },
    getAutofillStatus(state) {
        return state.autofillStatus;
    },
    getAddressCoordinates(state) {
        return state.addressSearchCoordinates;
    },
    getAddressSearchStatus(state) {
        return state.addressSearchStatus;
    },
    getCreatedHomeId(state) {
        return state.createdHomeId;
    },
    getHomeFormSections(state) {
        return {
            location: state.location,
            selectDetails: state.selectDetails,
            occupants: state.occupants,
            gad: state.gad,
            census: state.census,
            structure: state.structure,
            events: state.events,
            photos: state.photos,
            references: state.references,
            review: state.review,
            submit: state.submit,
        }
    },
};
